"use client";

import { useRef, useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SMALL, LABEL, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  CurrentDividerForm,
  type DividerStrings,
  VoltageDividerForm,
} from "@/components/tools/physics-waves/divider-forms";
import {
  NetworkOutcome,
  type NetworkViewStrings,
} from "@/components/tools/physics-waves/network-view";
import { QuantityField } from "@/components/tools/physics-waves/quantity-field";
import {
  RowList,
  type ValueRow,
} from "@/components/tools/physics-waves/row-list";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  leavesOf,
  listNetwork,
  type Network,
  type ParseErrorCode,
  parseNetwork,
} from "@/lib/tool-kit/physics-waves/resistor-network";
import {
  findUnit,
  OHM_UNITS,
  toBase,
  VOLT_UNITS,
} from "@/lib/tool-kit/physics-waves/units";

/** English strings. A translated content file overrides these through `ui`. */
export const SERIES_PARALLEL_RESISTOR_CALCULATOR_STRINGS = {
  modeLabel: "What are you combining?",
  modes: {
    series: "Series",
    parallel: "Parallel",
    nested: "Nested (series and parallel)",
    voltage: "Voltage divider",
    current: "Current divider",
  },
  rowLabel: "Resistor R{n}",
  unitFor: "Unit for {name}",
  remove: "Remove R{n}",
  add: "Add a resistor",
  expressionLabel: "Network",
  expressionHint:
    "Use + for series and | for parallel; | binds tighter than +, and brackets group. Values take k, M, m or the RKM code: 4.7k, 2k2, 4R7.",
  expressionPlaceholder: "100 + (220 | 330)",
  tryLabel: "Try an example",
  examples: {
    ladder: "100 + (220 | 330)",
    bridge: "(10 + 20) | (30 + 60)",
    three: "1k | 2k | 3k",
  },
  supplyLabel: "Supply voltage (optional)",
  supplyHint:
    "Add a voltage to see the current, voltage and power in each resistor.",
  sigFigs: "Round to this many significant figures",
  results: "Results",
  enterTwo: "Enter at least two resistances to combine.",
  enterAll: "Enter a number in every box to see the result.",
  needPositive: "Every resistance must be a number above zero.",
  equivalent: "Equivalent resistance",
  count: "Resistors",
  conductance: "Total conductance",
  totalCurrent: "Total current",
  totalPower: "Total power",
  working: "The working",
  seriesStep: "Series: {parts} = {result}",
  parallelStep: "Parallel: 1 ÷ ({parts}) = {result}",
  singleNote: "One resistor on its own: nothing to combine.",
  loadsHeading: "Each resistor with this supply",
  columnResistor: "Resistor",
  columnResistance: "Resistance",
  columnVoltage: "Voltage across",
  columnCurrent: "Current through",
  columnPower: "Power",
  resistorName: "R{n}",
  figureLabel: "Schematic of the resistor network",
  figureDescription:
    "The resistors drawn as a circuit, series parts side by side and parallel parts stacked, with an equivalent resistance of {total}.",
  parseErrors: {
    empty: "Type a network such as 100 + (220 | 330).",
    "bad-character":
      "“{token}” cannot be used here. Write values like 470, 4.7k or 2k2.",
    "bad-number":
      "“{token}” is not a resistance. Examples: 470, 4.7k, 2M2, 4R7.",
    "not-positive":
      "“{token}” is not above zero; every resistor needs a positive value.",
    "missing-paren":
      "A bracket is opened or closed without its partner (near “{token}”).",
    "unexpected-end":
      "The network ends too soon — is a value missing after + or |?",
    "unexpected-token":
      "Something is out of place near “{token}”. Join values with + or |.",
  },
  vdGoalLabel: "What do you want to find?",
  vdGoalOutput: "The output voltage",
  vdGoalPartner: "R2 for a target output",
  vdInput: "Input voltage",
  vdTarget: "Target output voltage",
  vdR1: "R1 (top)",
  vdR2: "R2 (bottom)",
  vdOutput: "Output voltage",
  vdRatio: "Fraction of the input",
  vdCurrent: "Current through both",
  vdPowerR1: "Power in R1",
  vdPowerR2: "Power in R2",
  vdPartnerNeeds:
    "The target must be above 0 and below the input voltage, and R1 must be above zero.",
  vdStepOutput:
    "Vout = Vin × R2 ÷ (R1 + R2) = {vin} × {r2} ÷ ({r1} + {r2}) = {vout}",
  vdStepCurrent: "I = Vin ÷ (R1 + R2) = {vin} ÷ {total} = {current}",
  vdStepPartner:
    "R2 = R1 × Vout ÷ (Vin − Vout) = {r1} × {vout} ÷ ({vin} − {vout}) = {r2}",
  cdTotal: "Total current",
  cdR1: "R1",
  cdR2: "R2",
  cdBranch1: "Current through R1",
  cdBranch2: "Current through R2",
  cdVoltage: "Voltage across both",
  cdShare1: "Share through R1",
  cdStep1: "I1 = I × R2 ÷ (R1 + R2) = {total} × {r2} ÷ {sum} = {i1}",
  cdStep2: "I2 = I − I1 = {total} − {i1} = {i2}",
  cdStepVoltage: "V = I1 × R1 = {i1} × {r1} = {volts}",
} as const;

type Strings = typeof SERIES_PARALLEL_RESISTOR_CALCULATOR_STRINGS;
type Mode = keyof Strings["modes"];

const SIG_CHOICES = ["3", "4", "5"] as const;
const NETWORK_MODES: readonly Mode[] = ["series", "parallel", "nested"];

const INITIAL_SERIES: ValueRow[] = [
  { id: 1, value: "100", unit: "Ω" },
  { id: 2, value: "220", unit: "Ω" },
  { id: 3, value: "330", unit: "Ω" },
];
const INITIAL_PARALLEL: ValueRow[] = [
  { id: 4, value: "220", unit: "Ω" },
  { id: 5, value: "330", unit: "Ω" },
];

type Built =
  | { kind: "ok"; network: Network }
  | { kind: "notice" }
  | { kind: "positive" }
  | { kind: "parse"; error: ParseErrorCode; token: string };

function buildFromRows(kind: "series" | "parallel", rows: ValueRow[]): Built {
  const values: number[] = [];
  for (const row of rows) {
    if (row.value.trim() === "") {
      continue;
    }
    const parsed = parseNumberInput(row.value);
    if (parsed === null || parsed <= 0) {
      return { kind: "positive" };
    }
    values.push(toBase(parsed, findUnit(OHM_UNITS, row.unit)));
  }
  const network = values.length < 2 ? null : listNetwork(kind, values);
  return network === null ? { kind: "notice" } : { kind: "ok", network };
}

function buildFromText(text: string): Built {
  const parsed = parseNetwork(text);
  return parsed.ok
    ? { kind: "ok", network: parsed.network }
    : { kind: "parse", error: parsed.error, token: parsed.token };
}

function NetworkMessage({
  built,
  strings,
}: {
  built: Exclude<Built, { kind: "ok" }>;
  strings: Strings;
}) {
  if (built.kind === "notice") {
    return <Notice>{strings.enterTwo}</Notice>;
  }
  if (built.kind === "positive") {
    return <ErrorNotice>{strings.needPositive}</ErrorNotice>;
  }
  if (built.error === "empty") {
    return <Notice>{strings.parseErrors.empty}</Notice>;
  }
  return (
    <ErrorNotice>
      {t(strings.parseErrors[built.error], { token: built.token })}
    </ErrorNotice>
  );
}

export default function SeriesParallelResistorCalculatorWidget() {
  const strings = useToolUi(SERIES_PARALLEL_RESISTOR_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("nested");
  const [seriesRows, setSeriesRows] = useState(INITIAL_SERIES);
  const [parallelRows, setParallelRows] = useState(INITIAL_PARALLEL);
  const [expression, setExpression] = useState("100 + (220 | 330)");
  const [supply, setSupply] = useState("12");
  const [supplyUnit, setSupplyUnit] = useState("V");
  const [sigText, setSigText] = useState<(typeof SIG_CHOICES)[number]>("4");
  const nextId = useRef(100);
  const fresh = () => {
    nextId.current += 1;
    return nextId.current;
  };
  const sig = Number(sigText);
  const isNetwork = NETWORK_MODES.includes(mode);

  let built: Built = { kind: "notice" };
  if (mode === "series") {
    built = buildFromRows("series", seriesRows);
  } else if (mode === "parallel") {
    built = buildFromRows("parallel", parallelRows);
  } else if (mode === "nested") {
    built = buildFromText(expression);
  }
  const supplyValue = parseNumberInput(supply);
  const volts =
    supplyValue === null || supplyValue <= 0
      ? null
      : toBase(supplyValue, findUnit(VOLT_UNITS, supplyUnit));

  const rowStrings = {
    rowLabel: strings.rowLabel,
    unitFor: strings.unitFor,
    remove: strings.remove,
    add: strings.add,
  };
  const viewStrings: NetworkViewStrings = strings;
  const dividerStrings: DividerStrings = strings;

  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="spr-mode"
        onChange={setMode}
        options={(Object.keys(strings.modes) as Mode[]).map((value) => ({
          value,
          label: strings.modes[value],
        }))}
        value={mode}
      />
      {mode === "series" ? (
        <RowList
          defaultUnit="Ω"
          idPrefix="spr-s"
          nextId={fresh}
          onChange={setSeriesRows}
          rows={seriesRows}
          strings={rowStrings}
          units={OHM_UNITS}
        />
      ) : null}
      {mode === "parallel" ? (
        <RowList
          defaultUnit="Ω"
          idPrefix="spr-p"
          nextId={fresh}
          onChange={setParallelRows}
          rows={parallelRows}
          strings={rowStrings}
          units={OHM_UNITS}
        />
      ) : null}
      {mode === "nested" ? (
        <>
          <TextField
            hint={strings.expressionHint}
            id="spr-expression"
            label={strings.expressionLabel}
            mono
            onChange={setExpression}
            placeholder={strings.expressionPlaceholder}
            value={expression}
          />
          <div className="flex flex-wrap items-center gap-2">
            <span className={LABEL}>{strings.tryLabel}</span>
            {(
              Object.keys(strings.examples) as (keyof Strings["examples"])[]
            ).map((key) => (
              <button
                className={`${BUTTON_SMALL} font-mono`}
                key={key}
                onClick={() => setExpression(strings.examples[key])}
                type="button"
              >
                {strings.examples[key]}
              </button>
            ))}
          </div>
        </>
      ) : null}
      <div className="grid items-start gap-4 sm:grid-cols-2">
        {isNetwork ? (
          <QuantityField
            hint={strings.supplyHint}
            id="spr-supply"
            label={strings.supplyLabel}
            onChange={setSupply}
            onUnitChange={setSupplyUnit}
            unit={supplyUnit}
            unitLabel={t(strings.unitFor, { name: strings.supplyLabel })}
            units={VOLT_UNITS}
            value={supply}
          />
        ) : null}
        <SelectField
          id="spr-sig"
          label={strings.sigFigs}
          onChange={setSigText}
          options={SIG_CHOICES.map((value) => ({ value, label: value }))}
          value={sigText}
        />
      </div>
      {isNetwork ? (
        <section aria-live="polite" className="space-y-4">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          {built.kind === "ok" ? (
            <NetworkOutcome
              count={leavesOf(built.network).length}
              fmt={fmt}
              network={built.network}
              sig={sig}
              strings={viewStrings}
              supply={volts}
            />
          ) : (
            <NetworkMessage built={built} strings={strings} />
          )}
        </section>
      ) : null}
      {mode === "voltage" ? (
        <VoltageDividerForm fmt={fmt} sig={sig} strings={dividerStrings} />
      ) : null}
      {mode === "current" ? (
        <CurrentDividerForm fmt={fmt} sig={sig} strings={dividerStrings} />
      ) : null}
    </>
  );
}
